import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, User, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const LOGO_URL = "/logo.png";

export const Register = () => {
  const { settings } = usePublicSettings();
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();
  const siteName = settings.site_name || "Frankly Talking English";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.register.errorPasswordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.register.errorPasswordTooShort'));
      return;
    }

    setIsLoading(true);

    const result = await register(name, email, password, phone || null);

    if (result.success) {
      toast.success(t('auth.register.toastCreatedTitle'), {
        description: t('auth.register.toastCreatedDescription')
      });
      navigate('/student/pending', { replace: true });
    } else {
      setError(result.error);
      toast.error(t('auth.register.toastFailedTitle'), {
        description: result.error
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] via-[#FDF8F3] to-[#FAFAF7] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#4CAF50]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#1A2E6E]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F5A623]/3 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-[#1A2E6E]/10 p-8 md:p-10">
          <div className="flex justify-center mb-8">
            <Link to="/">
              <img
                src={LOGO_URL}
                alt={siteName}
                className="h-14 w-auto"
              />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-[#1A2E6E] font-['Sora'] mb-2">
              {t('auth.register.title')}
            </h1>
            <p className="text-[#1A2E6E]/60">
              {t('auth.register.subtitle')}
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                {t('auth.register.fullNameLabel')}
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.register.fullNamePlaceholder')}
                  required
                  data-testid="register-name-input"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all text-[#1A2E6E] placeholder:text-[#9CA3AF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                {t('auth.register.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.register.emailPlaceholder')}
                  required
                  data-testid="register-email-input"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all text-[#1A2E6E] placeholder:text-[#9CA3AF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                {t('auth.register.phoneLabel')} <span className="text-[#1A2E6E]/40">{t('auth.register.phoneOptional')}</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('auth.register.phonePlaceholder')}
                  data-testid="register-phone-input"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all text-[#1A2E6E] placeholder:text-[#9CA3AF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                {t('auth.register.passwordLabel')}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.register.passwordPlaceholder')}
                  required
                  minLength={8}
                  data-testid="register-password-input"
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all text-[#1A2E6E] placeholder:text-[#9CA3AF]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                {t('auth.register.confirmPasswordLabel')}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.register.confirmPasswordPlaceholder')}
                  required
                  data-testid="register-confirm-password-input"
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all text-[#1A2E6E] placeholder:text-[#9CA3AF]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              data-testid="register-submit-btn"
              className="w-full py-4 rounded-xl bg-[#4CAF50] hover:bg-[#388E3C] text-white font-semibold shadow-lg shadow-[#4CAF50]/25 hover:shadow-xl hover:shadow-[#4CAF50]/30 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.register.submitting')}
                </>
              ) : (
                <>
                  {t('auth.register.submit')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-[#E8EAF0]" />
            <span className="text-sm text-[#1A2E6E]/40">{t('auth.register.alreadyHaveAccount')}</span>
            <div className="flex-1 h-px bg-[#E8EAF0]" />
          </div>

          <Link
            to="/login"
            data-testid="register-login-link"
            className="block w-full py-4 rounded-xl border-2 border-[#1A2E6E]/20 text-[#1A2E6E] font-semibold text-center hover:border-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-all duration-200"
          >
            {t('auth.register.signIn')}
          </Link>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-[#1A2E6E]/60 hover:text-[#D72B2B] transition-colors"
            >
              {`< ${t('common.backToHome')}`}
            </Link>
          </div>
        </div>

        <p className="text-center text-sm text-[#1A2E6E]/50 mt-6">
          {t('auth.register.termsNotice')}
        </p>
      </motion.div>
    </div>
  );
};

export default Register;
